import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Steps, Result, Alert, Typography, Spin } from 'antd';
import { SafetyCertificateOutlined, CheckCircleOutlined, ExportOutlined } from '@ant-design/icons';
import { startDigilocker, getDigilockerSession, refreshDigilocker, retryDigilockerFetch } from '../api';
import notificationStream from '../../notifications/stream';

const { Step } = Steps;
const { Paragraph, Text } = Typography;

// DigiLocker is event-driven end to end:
//   * Sandbox's webhook (or, as a fallback, the browser landing on our return
//     URL) settles the session on the SERVER, which also fetches the documents.
//   * The server pushes every state change to this tab over the realtime
//     channel (`kyc.digilocker`).
// This modal therefore never asks Sandbox anything on its own. It only re-reads
// our own session row (free) on a hint — the popup closing, the tab regaining
// focus — and, if the realtime channel is down, on a slow timer.
const POPUP_WATCH_INTERVAL_MS = 1000;   // free: just checks popup.closed
const DEGRADED_RESYNC_MS = 10000;       // free: reads our DB only, and only without a live stream

const extractErrorMessage = (error) => {
    const data = error.response?.data;
    if (!data) return error.message || 'An unexpected error occurred';
    if (data.error && typeof data.error === 'object' && data.error.message) {
        return `${data.message}: ${data.error.message}`;
    }
    return data.message || 'An unexpected error occurred';
};

const STEP_FOR_PHASE = { idle: 0, waiting: 1, fetching: 1, fetch_failed: 1, failed: 0, expired: 0, done: 2 };

/**
 * mode:
 *   'pre-check' -> new customer (no customerId; result is handed to the form)
 *   'linked'    -> existing customer (server writes the result onto the customer)
 */
const DigilockerModal = ({ visible, onClose, customerId, onSuccess, mode = 'pre-check' }) => {
    const [session, setSession] = useState(null); // public state from the backend
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [errorInfo, setErrorInfo] = useState(null);
    const [popupBlocked, setPopupBlocked] = useState(false);
    const [popupClosed, setPopupClosed] = useState(false);
    const [streamStatus, setStreamStatus] = useState(notificationStream.status);

    const popupRef = useRef(null);
    const popupWatchRef = useRef(null);
    const refRef = useRef(null);
    const deliveredRef = useRef(null); // ref whose result was already handed to onSuccess
    const onSuccessRef = useRef(onSuccess);
    useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

    const phase = session?.phase || 'idle';
    const inFlight = phase === 'waiting' || phase === 'fetching';

    // Refs only — stable for the component's life.
    const stopPopupWatch = useCallback(() => {
        if (popupWatchRef.current) { clearInterval(popupWatchRef.current); popupWatchRef.current = null; }
    }, []);

    const closePopup = useCallback(() => {
        stopPopupWatch();
        try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch (e) { /* cross-origin */ }
        popupRef.current = null;
    }, [stopPopupWatch]);

    // Single place every state update flows through (push, resync, actions).
    const applyState = useCallback((state) => {
        if (!state || (refRef.current && state.ref !== refRef.current)) return;
        setSession(state);
        if (state.phase !== 'waiting') closePopup();
        if (state.phase === 'done' && deliveredRef.current !== state.ref) {
            deliveredRef.current = state.ref;
            onSuccessRef.current?.(state.result || state);
        }
    }, [closePopup]);

    // Free: re-read our own session row (never calls Sandbox).
    const resync = useCallback(async () => {
        if (!refRef.current) return;
        try {
            applyState(await getDigilockerSession(refRef.current));
        } catch (e) {
            console.warn('[DigiLocker] resync failed:', e?.message);
        }
    }, [applyState]);

    // Primary: server push over the shared realtime channel.
    useEffect(() => {
        if (!session?.ref) return undefined;
        const off = notificationStream.subscribe(({ event, payload }) => {
            if (event === 'kyc.digilocker' && payload?.ref === refRef.current) applyState(payload);
        });
        const offStatus = notificationStream.onStatus(setStreamStatus);
        return () => { off(); offStatus(); };
    }, [session?.ref, applyState]);

    // Hints that something may have changed: return page postMessage, tab focus.
    useEffect(() => {
        if (!session?.ref) return undefined;
        const onMessage = (event) => {
            if (event.data?.type === 'digilocker-return' && event.data.ref === refRef.current) resync();
        };
        const onFocus = () => resync();
        window.addEventListener('message', onMessage);
        window.addEventListener('focus', onFocus);
        return () => { window.removeEventListener('message', onMessage); window.removeEventListener('focus', onFocus); };
    }, [session?.ref, resync]);

    // Safety net only when the realtime channel is NOT live: slow, free resync.
    useEffect(() => {
        if (!inFlight || streamStatus === 'live') return undefined;
        const timer = setInterval(resync, DEGRADED_RESYNC_MS);
        return () => clearInterval(timer);
    }, [inFlight, streamStatus, resync]);

    const reset = () => {
        closePopup();
        refRef.current = null;
        deliveredRef.current = null;
        setSession(null);
        setErrorInfo(null);
        setLoading(false);
        setChecking(false);
        setPopupBlocked(false);
        setPopupClosed(false);
    };

    useEffect(() => () => closePopup(), [closePopup]);
    useEffect(() => { if (!visible) reset(); }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    const openPopup = (url) => {
        if (!url) return false;
        const width = 520;
        const height = 680;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(url, 'digilocker_consent', `width=${width},height=${height},left=${left},top=${top}`);
        if (!popup) { setPopupBlocked(true); return false; }
        popupRef.current = popup;
        setPopupBlocked(false);
        setPopupClosed(false);
        stopPopupWatch();
        popupWatchRef.current = setInterval(() => {
            if (popupRef.current && popupRef.current.closed) {
                stopPopupWatch();
                setPopupClosed(true);
                resync(); // free
            }
        }, POPUP_WATCH_INTERVAL_MS);
        return true;
    };

    const handleStart = async () => {
        if (loading) return;
        reset();
        setLoading(true);
        try {
            // 1 billed call — or 0 if an open session for this customer is reused.
            const state = await startDigilocker(mode === 'linked' && customerId ? { customerId } : {});
            refRef.current = state.ref;
            applyState(state);
            if (state.phase === 'waiting') openPopup(state.authorizationUrl);
        } catch (error) {
            setErrorInfo({ message: extractErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    // Server-throttled to one Sandbox call per 30s; otherwise answers from our DB.
    const handleCheckStatus = async () => {
        if (!refRef.current) return;
        setChecking(true);
        try {
            const state = await refreshDigilocker(refRef.current);
            applyState(state);
            if (state.phase === 'waiting') {
                setErrorInfo({
                    type: 'info',
                    message: state.throttled
                        ? 'Just checked — DigiLocker has not confirmed consent yet. This updates by itself the moment it does.'
                        : 'DigiLocker has not confirmed consent yet. Finish it in the DigiLocker window; this updates by itself.'
                });
            } else {
                setErrorInfo(null);
            }
        } catch (error) {
            setErrorInfo({ message: extractErrorMessage(error) });
        } finally {
            setChecking(false);
        }
    };

    const handleRetryFetch = async () => {
        if (!refRef.current) return;
        setLoading(true);
        setErrorInfo(null);
        try {
            applyState(await retryDigilockerFetch(refRef.current));
        } catch (error) {
            setErrorInfo({ message: extractErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    // The mask, Esc and the X never silently drop a running verification.
    const handleModalClose = () => {
        if (!inFlight && !loading) { onClose(); return; }
        Modal.confirm({
            title: 'Close DigiLocker verification?',
            content: mode === 'linked'
                ? 'If the customer finishes consent, the details are still saved to this customer automatically.'
                : 'The customer may still be completing consent in the DigiLocker window. Closing now discards this verification.',
            okText: 'Close',
            okButtonProps: { danger: mode !== 'linked' },
            cancelText: 'Keep waiting',
            onOk: () => { reset(); onClose(); }
        });
    };

    // Cancel = back to the start screen (the session simply expires server-side).
    const handleCancel = () => {
        Modal.confirm({
            title: 'Cancel this DigiLocker verification?',
            okText: 'Cancel verification',
            okButtonProps: { danger: true },
            cancelText: 'Keep waiting',
            onOk: reset
        });
    };

    const renderAlert = () => errorInfo && (
        <Alert type={errorInfo.type || 'error'} message={errorInfo.message} showIcon style={{ marginTop: 16, textAlign: 'left' }} />
    );

    const renderContent = () => {
        if (phase === 'done') {
            return (
                <Result
                    status="success"
                    title="Identity Verified!"
                    subTitle={mode === 'linked' ? 'DigiLocker details saved to this customer.' : 'DigiLocker consent completed and details fetched.'}
                    icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                />
            );
        }
        if (phase === 'fetching' || phase === 'fetch_failed') {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    {phase === 'fetching' ? <Spin size="large" /> : null}
                    <Paragraph style={{ marginTop: 16 }}>
                        {phase === 'fetching'
                            ? 'Consent received. Fetching Aadhaar & PAN details from DigiLocker…'
                            : 'Consent was received, but fetching the details failed.'}
                    </Paragraph>
                    {phase === 'fetch_failed' && session?.fetchError && (
                        <Alert type="error" showIcon message={session.fetchError} style={{ textAlign: 'left' }} />
                    )}
                    {renderAlert()}
                </div>
            );
        }
        if (phase === 'waiting') {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin size="large" />
                    <Paragraph style={{ marginTop: 16 }}>
                        Waiting for the customer to complete consent in the DigiLocker window…
                    </Paragraph>
                    <Text type="secondary">This updates automatically the moment DigiLocker confirms.</Text>
                    {popupBlocked && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 16, textAlign: 'left' }}
                            message="The browser blocked the DigiLocker window. Press “Open DigiLocker” to open it."
                        />
                    )}
                    {popupClosed && !popupBlocked && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginTop: 16, textAlign: 'left' }}
                            message="The DigiLocker window was closed. If consent was completed this will update by itself; otherwise reopen DigiLocker."
                        />
                    )}
                    {streamStatus !== 'live' && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                            Live updates are reconnecting — checking our server every {DEGRADED_RESYNC_MS / 1000}s meanwhile.
                        </Text>
                    )}
                    {renderAlert()}
                </div>
            );
        }
        // idle / failed / expired
        return (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <SafetyCertificateOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                {phase === 'failed' && (
                    <Alert type="warning" showIcon style={{ marginBottom: 16, textAlign: 'left' }}
                        message="Consent was denied or could not be completed in DigiLocker." />
                )}
                {phase === 'expired' && (
                    <Alert type="warning" showIcon style={{ marginBottom: 16, textAlign: 'left' }}
                        message="That DigiLocker session expired. Start again for a fresh link." />
                )}
                <Paragraph>
                    The customer will sign in to <Text strong>DigiLocker</Text> and consent to share their Aadhaar &amp; PAN.
                    A DigiLocker window will open.
                </Paragraph>
                {renderAlert()}
            </div>
        );
    };

    const footer = [];
    if (phase === 'idle' || phase === 'failed' || phase === 'expired') {
        footer.push(
            <Button key="start" type="primary" onClick={handleStart} loading={loading}>
                {phase === 'idle' ? 'Continue to DigiLocker' : 'Start again'}
            </Button>
        );
    }
    if (phase === 'waiting') {
        footer.push(
            <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
            <Button key="reopen" icon={<ExportOutlined />} onClick={() => openPopup(session?.authorizationUrl)}>
                {popupBlocked ? 'Open DigiLocker' : 'Reopen DigiLocker'}
            </Button>,
            <Button key="check" type="primary" onClick={handleCheckStatus} loading={checking}>Check status</Button>
        );
    }
    if (phase === 'fetch_failed') {
        footer.push(
            <Button key="restart" onClick={handleStart} loading={loading && !session?.canRetryFetch}>Start over</Button>,
            <Button key="retry" type="primary" onClick={handleRetryFetch} loading={loading}>Retry fetching details</Button>
        );
    }
    if (phase === 'done') {
        footer.push(<Button key="close" type="primary" onClick={onClose}>Close</Button>);
    }

    return (
        <Modal
            title="DigiLocker eKYC Verification"
            open={visible}
            onCancel={handleModalClose}
            maskClosable={false}
            keyboard={false}
            footer={footer}
        >
            <Steps current={STEP_FOR_PHASE[phase] ?? 0} size="small">
                <Step title="Consent" />
                <Step title={phase === 'fetching' ? 'Fetching' : 'Verifying'} />
                <Step title="Complete" />
            </Steps>
            {renderContent()}
        </Modal>
    );
};

export default DigilockerModal;
